<template>
  <div
    class="min-h-[100dvh] max-h-[100dvh] font-inter bg-pitch-black text-total-white flex flex-col"
  >
    <!-- Top Navigation -->
    <TopNavbar
      :isThread="true"
      :userRole="userRole"
      :projectId="project?.id"
      :isSimulation="project?.is_simulation"
      :projectName="project?.address_1"
    />

    <div class="flex-1 overflow-y-auto">
      <div
        class="flex items-center justify-between space-x-2 px-4 bg-pitch-black py-3 mt-1"
      >
        <button @click="goBack" class="text-prime-golden">
          <img
            src="@/assets/images/left-chevron.svg"
            class="fas w-4 fa-arrow-left"
            alt="Back"
          />
        </button>
        <!-- Optional Delete Button (if needed) -->
        <button class="text-prime-golden">
          <img src="@/assets/images/trash-icon.svg" class="w-[18px]" alt="Delete" />
        </button>
      </div>

      <!-- Main Content Area (scrollable) -->
      <div class="flex-1">
        <!-- Calendar Log Container -->
        <div
          v-if="calendarLog"
          class="bg-pitch-black p-4 rounded-lg flex flex-col items-start text-left"
        >
          <!-- Subject & Last Updated -->

          <h1 class="text-2xl font-bold mb-1">
            <img
              src="@/assets/images/calendar-icon.svg"
              class="w-10 h-10 inline-block align-sub"
              alt="Calendar"
            />
            {{ calendarLog.subject }}
          </h1>
          <p class="text-sm text-gray-500 mb-2">
            Created on {{ formatDate(calendarLog.created_at) }}
          </p>

          <!-- The first communication: event start/end times & content -->
          <div v-if="calendarEvent" class="text-left mb-2">
            <p class="text-lg font-semibold mb-1 mt-4">Details</p>
            <p class="text-sm mb-8 mt-1 text-gray-300">{{ calendarEvent.content }}</p>

            <p class="text-lg font-semibold mb-1 mt-4">
              <i class="far fa-clock mr-2 text-prime-golden"></i>Time
            </p>
            <p class="text-sm mb-1">
              {{ formattedDate }}
            </p>
            <p class="text-sm my-1">
              {{ formattedTimeRange }}
            </p>

            <!-- Location section - only shown if event_location exists -->
            <div class="mt-4">
              <p class="text-lg font-semibold mb-1">
                <img
                  src="@/assets/images/location-icon.svg"
                  class="w-[22px] h-[22px] inline-block mr-2 ml-[-2px]"
                  alt="Location"
                />Location
              </p>
              <p class="text-sm mb-3">
                {{ calendarEvent.event_location || "No location provided" }}
              </p>
            </div>
          </div>

          <!-- Participants -->
          <h3 class="text-lg font-semibold mb-1 mt-4">
            Participants ({{ participantsCount }})
          </h3>
          <ul class="list-none ml-0 mb-3 mt-1">
            <li
              v-for="participant in calendarLog.participants"
              :key="participant.id"
              class="mb-2"
            >
              <router-link
                :to="{
                  name: 'ContactViewPage',
                  params: { id: participant.id },
                  query: { from_calendar: true },
                }"
                class="text-prime-golden underline text-base"
              >
                {{ participant.first_name }} {{ participant.last_name }}
              </router-link>
              <span
                v-if="
                  participant.role &&
                  participant.role !==
                    participant.first_name + ' ' + participant.last_name
                "
                class="text-xs text-gray-300 ml-3"
              >
                {{ participant.role }}
              </span>
            </li>
          </ul>
        </div>

        <!-- Loader if calendarLog is not yet loaded -->
        <div v-else class="text-left p-4 text-gray-400 mt-4">
          <p>Loading calendar log...</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import TopNavbar from "@/components/TopNavbar.vue";
import { useLogStore } from "@/stores/log";
import { useUserStore } from "@/stores/user";
import { useProjectStore } from "@/stores/project";

const route = useRoute();
const router = useRouter();
const logStore = useLogStore();
const userStore = useUserStore();
const projectStore = useProjectStore();

const userRole = computed(() => userStore.profile?.account_type);
const project = computed(() => projectStore.currentProject);

const calendarLog = ref(null);

const calendarEvent = computed(() => {
  if (!calendarLog.value?.communications) return null;
  return calendarLog.value.communications[0] || null;
});

onMounted(async () => {
  const threadId = route.query.thread_id;
  if (!threadId) return;

  try {
    await logStore.fetchMessagesByThreadId(threadId, "asc", 1);

    // For demonstration, we just check if the first message has "comm_type=calendar"
    const firstItem = logStore.threadMessages[0];

    if (firstItem && firstItem.comm_type === "calendar") {
      // We'll build the object shape that matches your example:
      calendarLog.value = {
        subject: firstItem.subject,
        created_at: firstItem.date_time_created,
        participants: logStore.stakeholders || [], // or store-level participants
        communications: [
          {
            comm_type: "calendar",
            content: firstItem.content,
            event_start_time: firstItem.event_start_time,
            event_end_time: firstItem.event_end_time,
            event_location: firstItem.event_location,
            participants: firstItem.participants || [],
          },
        ],
      };
    }

    // calendarLog.value = {
    //   project_id: 581,
    //   comm_type: "calendar",
    //   subject: "Kickoff Meeting: Dave Liam & Ca La",
    //   id: 607,
    //   last_viewed_at: null,
    //   last_updated_at: "2025-03-03T00:44:20.178185",
    //   participants: [
    //     {
    //       first_name: "Project",
    //       last_name: "Agent",
    //       email: "pa3@swiftcraft.tech",
    //       phone: "+14153196056",
    //       role: "Project Agent",
    //       id: 1529,
    //     },
    //     {
    //       first_name: "Dave",
    //       last_name: "Liam",
    //       email: "justcallmealplease@gmail.com",
    //       phone: "+14158810731",
    //       role: "Homeowner",
    //       id: 1534,
    //     },
    //     {
    //       first_name: "Ca",
    //       last_name: "La",
    //       email: "camilaloureiromn@gmail.com",
    //       phone: "+16282310362",
    //       role: "Designer",
    //       id: 1414,
    //     },
    //   ],
    //   communications: [
    //     {
    //       comm_type: "calendar",
    //       content: "Kickoff meeting to discuss the full kitchen remodel project.",
    //       to_contact: "justcallmealplease@gmail.com, camilaloureiromn@gmail.com",
    //       from_contact: "pa3@swiftcraft.tech",
    //       event_start_time: "2025-03-24T16:00:00",
    //       event_end_time: "2025-03-24T17:00:00",
    //       id: 1637,
    //       date_time_created: "2025-03-03T00:44:20.016785",
    //     },
    //   ],
    // };
  } catch (error) {
    console.error("Error fetching calendar thread:", error);
  }
});

const formattedDate = computed(() => {
  if (!calendarEvent.value?.event_start_time) return "";
  return new Date(calendarEvent.value.event_start_time).toLocaleDateString("en-US", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
});

const formattedTimeRange = computed(() => {
  if (!calendarEvent.value?.event_start_time || !calendarEvent.value?.event_end_time)
    return "";
  const start = new Date(calendarEvent.value.event_start_time).toLocaleTimeString(
    "en-US",
    {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }
  );
  const end = new Date(calendarEvent.value.event_end_time).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return `${start} - ${end}`;
});

const participantsCount = computed(() => {
  return calendarLog.value?.participants?.length || 0;
});

function goBack() {
  if (route.query.tab) {
    router.push({
      name: "ProjectPage",
      params: { id: route.query.projectId || route.params.projectId || route.params.id },
      query: { tab: route.query.tab, readLogs: route.query.readLogs },
    });
  } else {
    router.go(-1);
  }
}

function formatDate(dateStr) {
  return dateStr
    ? new Date(dateStr).toLocaleString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
    : "";
}
</script>

<style scoped></style>
